import React, { useState, useEffect, useMemo } from 'react'
import { ClockIcon, PlayIcon, SquareIcon, CalendarIcon, UserIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import {
  clockIn,
  clockOut,
  fetchActiveTimeEntry,
  resolveEmployeeIdForUser,
  startJobIfNeeded,
  FieldGpsError,
} from '../lib/timeClockActions'
import { ColumnSelector } from '../components/ColumnSelector'
import { useColumnSelector } from '../hooks/useColumnSelector'
import { usePermissions } from '../hooks/usePermissions'
import {
  fetchJobsByIds,
  resolveAssignedJobsScope,
  canViewAllJobs,
} from '../lib/jobVisibility'
import { getAppLocale } from '../i18n'
import { formatFieldDate } from '../i18n/localeFormat'
import { MobilePage, MobilePageHeader, mobile } from '../components/mobile'

export function TimeClock() {
  const { t } = useTranslation('field')
  const locale = getAppLocale()
  const { user } = useAuth()
  const { can, isAdmin, loading: permLoading } = usePermissions()
  const viewAllJobs = canViewAllJobs(can, isAdmin)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [employeeId, setEmployeeId] = useState<string | null>(null)
  const [employeeLoading, setEmployeeLoading] = useState(true)
  const [isClockedIn, setIsClockedIn] = useState(false)
  const [clockInTime, setClockInTime] = useState<Date | null>(null)
  const [activeEntryId, setActiveEntryId] = useState<string | null>(null)
  const [selectedJob, setSelectedJob] = useState('')
  const [notes, setNotes] = useState('')
  const [startJobToo, setStartJobToo] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [timeEntries, setTimeEntries] = useState<{
    id: number | string
    date: string
    clockIn: string
    clockOut: string
    job: string
    customer: string
    duration: number
    notes: string
  }[]>([])

  const [jobs, setJobs] = useState<any[]>([])

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    const resolveEmployee = async () => {
      if (!user?.id) {
        setEmployeeLoading(false)
        return
      }
      try {
        const empId = await resolveEmployeeIdForUser(user.id)
        setEmployeeId(empId)
      } catch (err) {
        console.error('Error resolving employee for user:', err)
        setEmployeeId(null)
      } finally {
        setEmployeeLoading(false)
      }
    }
    resolveEmployee()
  }, [user?.id])

  useEffect(() => {
    if (permLoading) return

    const fetchJobs = async () => {
      const jobSelect = 'id, job_number, title, status, customers(company_name)'
      let query = supabase
        .from('jobs')
        .select(jobSelect)
        .in('status', ['scheduled', 'in_progress'])
        .order('scheduled_date', { ascending: true })

      let extraJobIds: string[] = []
      if (!viewAllJobs) {
        if (!user?.id) {
          setJobs([])
          return
        }
        const empId = await resolveEmployeeIdForUser(user.id)
        if (!empId) {
          setJobs([])
          return
        }
        const scope = await resolveAssignedJobsScope(empId)
        query = query.or(scope.orFilter)
        extraJobIds = scope.extraJobIds
      }

      const { data, error } = await query

      if (error) {
        console.error('Error fetching jobs:', error)
        return
      }

      const byId = new Map<string, any>()
      for (const row of data || []) byId.set(row.id, row)

      if (extraJobIds.length > 0) {
        try {
          const extras = await fetchJobsByIds(extraJobIds, jobSelect)
          for (const row of extras) {
            if (!['scheduled', 'in_progress'].includes(String(row.status || ''))) continue
            if (typeof row.id === 'string') byId.set(row.id, row)
          }
        } catch (extraErr) {
          console.error('Error fetching assigned jobs:', extraErr)
        }
      }

      setJobs(Array.from(byId.values()))
    }

    const fetchTimeEntries = async (empId: string) => {
      try {
        const { data, error } = await supabase
          .from('time_entries')
          .select('*, job:jobs(title, job_number, customer:customers(company_name))')
          .eq('employee_id', empId)
          .order('clock_in_time', { ascending: false })
          .limit(20)

        if (error) throw error

        const mapped = (data || []).map((entry: any) => {
          const clockIn = entry.clock_in_time ? new Date(entry.clock_in_time) : null
          const clockOut = entry.clock_out_time ? new Date(entry.clock_out_time) : null
          const duration = entry.total_hours || (clockIn && clockOut
            ? (clockOut.getTime() - clockIn.getTime()) / (1000 * 60 * 60)
            : 0)

          return {
            id: entry.id,
            date: clockIn ? clockIn.toISOString().split('T')[0] : '',
            clockIn: clockIn ? clockIn.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }) : '',
            clockOut: clockOut ? clockOut.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }) : '',
            job: entry.job?.title || entry.job?.job_number || '',
            customer: entry.job?.customer?.company_name || '',
            duration: Math.round(duration * 100) / 100,
            notes: entry.notes || '',
          }
        })
        setTimeEntries(mapped)
      } catch (err) {
        console.error('Error fetching time entries:', err)
      }
    }

    const checkActiveEntry = async (empId: string) => {
      try {
        const entry = await fetchActiveTimeEntry(empId)
        if (entry) {
          setIsClockedIn(true)
          setClockInTime(new Date(entry.clock_in_time))
          setActiveEntryId(entry.id)
          setSelectedJob(entry.job_id || '')
          setNotes(entry.notes || '')
        }
      } catch (err) {
        console.error('Error checking active entry:', err)
      }
    }

    fetchJobs()
    if (employeeId) {
      fetchTimeEntries(employeeId)
      checkActiveEntry(employeeId)
    }
  }, [employeeId, user?.id, viewAllJobs, permLoading])

  const selectedJobRecord = useMemo(() => jobs.find((j) => j.id === selectedJob), [jobs, selectedJob])
  const selectedJobNeedsStart = !!selectedJobRecord && selectedJobRecord.status !== 'in_progress'

  const handleClockIn = async () => {
    if (!selectedJob) {
      alert('Please select a job before clocking in')
      return
    }
    if (!employeeId) {
      alert('Your login is not linked to an employee record yet. Ask an admin to link it in Team settings.')
      return
    }

    try {
      setSubmitting(true)
      const { entry, geo } = await clockIn({
        employeeId,
        jobId: selectedJob,
        notes,
        entryType: 'job',
      })

      if (startJobToo && selectedJobRecord && selectedJobRecord.status !== 'in_progress') {
        await startJobIfNeeded(selectedJob, selectedJobRecord.status, geo, employeeId)
        setJobs((prev) => prev.map((j) => (j.id === selectedJob ? { ...j, status: 'in_progress' } : j)))
      }

      setIsClockedIn(true)
      setClockInTime(new Date(entry.clock_in_time))
      setActiveEntryId(entry.id)
    } catch (err: any) {
      console.error('Error clocking in:', err)
      if (err instanceof FieldGpsError) {
        if (err.code !== 'cancelled') alert(err.message)
        return
      }
      alert('Failed to clock in: ' + (err.message || 'Unknown error'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleClockOut = async () => {
    if (!clockInTime || !activeEntryId) return

    try {
      setSubmitting(true)
      const { totalHours } = await clockOut(activeEntryId, notes)
      const clockOutTime = new Date()

      const job = jobs.find((j) => j.id === selectedJob)
      const newEntry = {
        id: activeEntryId,
        date: clockInTime.toISOString().split('T')[0],
        clockIn: clockInTime.toLocaleTimeString('en-US', {
          hour12: false,
          hour: '2-digit',
          minute: '2-digit',
        }),
        clockOut: clockOutTime.toLocaleTimeString('en-US', {
          hour12: false,
          hour: '2-digit',
          minute: '2-digit',
        }),
        job: job?.title || '',
        customer: job?.customers?.company_name || '',
        duration: totalHours,
        notes: notes,
      }

      setTimeEntries([newEntry, ...timeEntries])
      setIsClockedIn(false)
      setClockInTime(null)
      setActiveEntryId(null)
      setSelectedJob('')
      setNotes('')
    } catch (err: any) {
      console.error('Error clocking out:', err)
      if (err instanceof FieldGpsError) {
        if (err.code !== 'cancelled') alert(err.message)
        return
      }
      alert('Failed to clock out: ' + (err.message || 'Unknown error'))
    } finally {
      setSubmitting(false)
    }
  }

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  const formatDate = (date: Date) => {
    return formatFieldDate(date, locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const getElapsedTime = () => {
    if (!clockInTime) return '00:00:00'
    const elapsed = new Date().getTime() - clockInTime.getTime()
    const hours = Math.floor(elapsed / (1000 * 60 * 60))
    const minutes = Math.floor((elapsed % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((elapsed % (1000 * 60)) / 1000)
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`
  }

  const TIME_CLOCK_COLUMNS = [
    { id: 'date', label: 'Date' },
    { id: 'job', label: 'Job' },
    { id: 'customer', label: 'Customer' },
    { id: 'time', label: 'Time' },
    { id: 'duration', label: 'Duration' },
    { id: 'notes', label: 'Notes' },
  ]
  const { visibleColumns, toggleColumn } = useColumnSelector(TIME_CLOCK_COLUMNS, { storageKey: 'time_clock' })

  return (
    <MobilePage>
      <MobilePageHeader title={t('timeClock.title')} subtitle={t('timeClock.subtitle')} />

      {!employeeLoading && !employeeId && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm">
          {t('timeClock.employeeNotLinkedBanner')}
        </div>
      )}

      {/* Current Time Display */}
      <div className={`${mobile.surface} p-5 sm:p-8 text-center`}>
          <div className="mb-2 sm:mb-4">
            <h2 className="text-base sm:text-2xl font-semibold text-gray-900">{formatDate(currentTime)}</h2>
            <div className="text-4xl sm:text-6xl font-mono font-bold text-blue-600 mt-2 sm:mt-4">
              {formatTime(currentTime)}
            </div>
          </div>

          {isClockedIn && (
            <div className="mt-3 sm:mt-4">
              <div className="text-sm sm:text-lg text-gray-600 mb-1 sm:mb-2">{t('timeClock.timeElapsed')}</div>
              <div className="text-3xl sm:text-4xl font-mono font-bold text-green-600">{getElapsedTime()}</div>
            </div>
          )}
        </div>

      {/* Clock In/Out Controls */}
      <div className={`${mobile.surface} p-4 sm:p-6`}>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">
            {t('timeClock.clockIn')} / {t('timeClock.clockOut')}
          </h2>

          {!isClockedIn ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('timeClock.selectJobRequired')}</label>
                <select
                  value={selectedJob}
                  onChange={(e) => setSelectedJob(e.target.value)}
                  className={mobile.field}
                >
                  <option value="">{t('timeClock.chooseJob')}</option>
                  {jobs.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.job_number} - {job.title} - {job.customers?.company_name || t('timeClock.noCustomer')}
                    </option>
                  ))}
                </select>
              </div>
              {selectedJobNeedsStart && (
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={startJobToo}
                    onChange={(e) => setStartJobToo(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  {t('timeClock.startJobMarkProgress')}
                </label>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('timeClock.notesOptional')}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder={t('timeClock.notesPlaceholder')}
                  className={mobile.field}
                />
              </div>
              <button
                onClick={handleClockIn}
                disabled={submitting}
                className={`${mobile.successBtn} w-full sm:w-auto text-lg`}
              >
                <PlayIcon className="w-6 h-6" />
                {submitting ? t('timeClock.clockIn') + '…' : t('timeClock.clockIn')}
              </button>
            </div>
          ) : (
            <div className="text-center">
              <div className="mb-4">
                <div className="text-lg text-gray-600">{t('timeClock.currentlyWorkingOn')}</div>
                <div className="text-xl font-semibold text-gray-900">
                  {jobs.find((j) => j.id === selectedJob)?.title}
                </div>
                <div className="text-sm text-gray-500">
                  {jobs.find((j) => j.id === selectedJob)?.customers?.company_name || t('timeClock.noCustomer')}
                </div>
              </div>
              <button
                onClick={handleClockOut}
                disabled={submitting}
                className={`${mobile.dangerBtn} w-full sm:w-auto text-lg mx-auto`}
              >
                <SquareIcon className="w-6 h-6" />
                {submitting ? t('timeClock.clockOut') + '…' : t('timeClock.clockOut')}
              </button>
            </div>
          )}
        </div>

      {/* Today's Stats */}
      <div className="grid grid-cols-3 gap-2 sm:gap-6">
        <div className={`${mobile.surface} p-3 sm:p-6`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Today&apos;s Hours</p>
              <p className="mt-1 sm:mt-2 text-lg sm:text-2xl font-bold text-gray-900">
                {timeEntries
                  .filter((entry) => entry.date === currentTime.toISOString().split('T')[0])
                  .reduce((sum, entry) => sum + entry.duration, 0)
                  .toFixed(1)}
                h
              </p>
            </div>
            <div className="hidden sm:block p-3 rounded-lg bg-blue-50 text-blue-600">
              <ClockIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className={`${mobile.surface} p-3 sm:p-6`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Jobs Today</p>
              <p className="mt-1 sm:mt-2 text-lg sm:text-2xl font-bold text-gray-900">
                {timeEntries.filter(
                  (entry) => entry.date === currentTime.toISOString().split('T')[0]
                ).length}
              </p>
            </div>
            <div className="hidden sm:block p-3 rounded-lg bg-green-50 text-green-600">
              <CalendarIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className={`${mobile.surface} p-3 sm:p-6`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm font-medium text-gray-600">Status</p>
                <p className="mt-1 sm:mt-2 text-sm sm:text-2xl font-bold text-gray-900 truncate">
                  {isClockedIn ? 'In' : 'Out'}
                </p>
                <p className={`text-xs sm:text-sm ${isClockedIn ? 'text-green-600' : 'text-gray-500'}`}>
                  {isClockedIn ? 'Working' : 'Not Working'}
                </p>
              </div>
              <div
                className={`hidden sm:block p-3 rounded-lg ${
                  isClockedIn ? 'bg-green-50 text-green-600' : 'bg-gray-50 text-gray-600'
                }`}
              >
                <UserIcon className="w-6 h-6" />
              </div>
            </div>
          </div>
        </div>

      {/* Recent Time Entries */}
      <div className={`${mobile.surface} overflow-hidden`}>
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Recent Time Entries</h2>
            <div className="hidden lg:block">
              <ColumnSelector
                columns={TIME_CLOCK_COLUMNS}
                visibleColumns={visibleColumns}
                onToggle={toggleColumn}
              />
            </div>
          </div>

          {/* Mobile cards */}
          <div className="native-mobile-list divide-y divide-gray-100">
            {timeEntries.slice(0, 10).map((entry) => (
              <div key={entry.id} className="px-4 py-3 space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{entry.job || 'No job'}</p>
                    <p className="text-xs text-gray-500 truncate">{entry.customer || '—'}</p>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 flex-shrink-0">{entry.duration}h</span>
                </div>
                <p className="text-xs text-gray-600">
                  {entry.date ? new Date(entry.date).toLocaleDateString() : '—'} · {entry.clockIn}
                  {entry.clockOut ? ` – ${entry.clockOut}` : ' – …'}
                </p>
                {entry.notes ? <p className="text-xs text-gray-500 line-clamp-2">{entry.notes}</p> : null}
              </div>
            ))}
            {timeEntries.length === 0 && (
              <p className="px-4 py-8 text-sm text-gray-500 text-center">No time entries yet.</p>
            )}
          </div>

          <div className="native-desktop-table overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {visibleColumns.has('date') && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                  )}
                  {visibleColumns.has('job') && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Job
                    </th>
                  )}
                  {visibleColumns.has('customer') && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                  )}
                  {visibleColumns.has('time') && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Time
                    </th>
                  )}
                  {visibleColumns.has('duration') && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                  )}
                  {visibleColumns.has('notes') && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Notes
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {timeEntries.slice(0, 10).map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    {visibleColumns.has('date') && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(entry.date).toLocaleDateString()}
                      </td>
                    )}
                    {visibleColumns.has('job') && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.job}
                      </td>
                    )}
                    {visibleColumns.has('customer') && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.customer}
                      </td>
                    )}
                    {visibleColumns.has('time') && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.clockIn} - {entry.clockOut}
                      </td>
                    )}
                    {visibleColumns.has('duration') && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {entry.duration}h
                      </td>
                    )}
                    {visibleColumns.has('notes') && (
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                        {entry.notes}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
    </MobilePage>
  )
}
